import {
  BookOpen,
  Calendar,
  ChartLineUp,
  CreditCard,
  GearSix,
  House,
  IdentificationCard,
  Image,
  PaperPlaneRight,
  PencilSimple,
  ShareNetwork,
  YoutubeLogo,
} from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { env } from '@/config/env';

/*
 * This module exports navigation data, not components — the .tsx extension is
 * only needed because the icons are JSX. Fast refresh has nothing to preserve
 * here, so its "one component per file" rule does not apply.
 */
export interface NavItem {
  key: string;
  path: string;
  label: string;
  icon: ReactNode;
  /** Nested routes that should still highlight this item. */
  matchPrefix?: string;
  /**
   * Further prefixes that highlight this item without being its destination —
   * the old path of a screen that moved, so a bookmark mid-redirect does not
   * light up nothing.
   */
  alsoMatch?: string[];
}

/**
 * Ten items, no groups — up to twelve where Analytics and the media kit are
 * part of the build.
 *
 * Those two are build-time switches, off by default. Until one is turned on
 * its item is not in this list at all, rather than present and leading
 * nowhere — and written as a conditional spread so the build drops it, rather
 * than as an entry filtered out when the app starts.
 */
export const NAV_ITEMS: NavItem[] = [
  {
    key: 'home',
    path: '/app',
    label: 'Home',
    icon: <House size={18} />,
  },
  {
    key: 'posts',
    path: '/app/posts',
    label: 'Posts',
    icon: <PencilSimple size={18} />,
    matchPrefix: '/app/posts',
  },
  {
    key: 'youtube',
    path: '/app/youtube',
    label: 'YouTube',
    icon: <YoutubeLogo size={18} />,
    matchPrefix: '/app/youtube',
  },
  {
    key: 'delivery',
    path: '/app/distributions',
    label: 'Delivery',
    icon: <PaperPlaneRight size={18} />,
    matchPrefix: '/app/distributions',
  },
  ...(env.analyticsEnabled
    ? [
        {
          key: 'analytics',
          path: '/app/analytics',
          label: 'Analytics',
          icon: <ChartLineUp size={18} />,
          matchPrefix: '/app/analytics',
        },
      ]
    : []),
  {
    key: 'libraries',
    path: '/app/libraries',
    label: 'Libraries',
    icon: <BookOpen size={18} />,
    matchPrefix: '/app/libraries',
  },
  {
    key: 'schedule',
    path: '/app/schedule',
    label: 'Schedule',
    icon: <Calendar size={18} />,
    matchPrefix: '/app/schedule',
    alsoMatch: ['/app/upcoming', '/app/cadence'],
  },
  {
    key: 'accounts',
    path: '/app/social-accounts',
    label: 'Accounts',
    icon: <ShareNetwork size={18} />,
    matchPrefix: '/app/social-accounts',
    alsoMatch: ['/app/accounts'],
  },
  ...(env.mediaKitEnabled
    ? [
        {
          key: 'media-kit',
          path: '/app/media-kit',
          label: 'Media Kit',
          icon: <IdentificationCard size={18} />,
          matchPrefix: '/app/media-kit',
        },
      ]
    : []),
  {
    key: 'media',
    path: '/app/media',
    label: 'Media',
    icon: <Image size={18} />,
    matchPrefix: '/app/media',
  },
  {
    key: 'billing',
    path: '/app/billing',
    label: 'Billing',
    icon: <CreditCard size={18} />,
    matchPrefix: '/app/billing',
    alsoMatch: ['/app/settings/billing'],
  },
  {
    key: 'settings',
    path: '/app/settings',
    label: 'Settings',
    icon: <GearSix size={18} />,
    matchPrefix: '/app/settings',
  },
];

/** Longest matching prefix wins, so /settings/blogs highlights Settings. */
export function findActiveNavKey(pathname: string): string {
  const candidates = NAV_ITEMS.flatMap((item) =>
    [item.matchPrefix, ...(item.alsoMatch ?? [])]
      .filter((prefix): prefix is string => Boolean(prefix))
      .filter((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
      .map((prefix) => ({ key: item.key, length: prefix.length }))
  ).sort((a, b) => b.length - a.length);

  return candidates[0]?.key ?? (pathname === '/app' ? 'home' : '');
}
